import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Divider
} from '@mui/material';
import {
  People,
  SportsCricket,
  SportsTennis,
  SwapHoriz,
  CalendarMonth,
  EmojiEvents,
  Bolt,
  Lock
} from '@mui/icons-material';
import type { League } from '../../types/database';

interface LeagueRulesDisplayProps {
  league: League;
}

const LeagueRulesDisplay: React.FC<LeagueRulesDisplayProps> = ({ league }) => {
  const isTennis = league.sport === 'tennis';
  const tc = league.tennisConfig;
  const tr = league.tennisSquadRules;
  const menMults = tr?.menMultipliers ?? [2, 1.75, 1.5, 1.25, 1];
  const womenMults = tr?.womenMultipliers ?? [2, 1.75, 1.5, 1.25, 1];

  return (
    <Box>
      {/* Points System */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2, md: 2.5 } }}>
          <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }} mb={{ xs: 1.5, sm: 2 }}>
            <EmojiEvents color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Points System
            </Typography>
          </Box>

          {isTennis ? (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Match Win</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {tc?.matchWinPoints ?? 20} points
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Straight Sets Bonus</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  +{tc?.straightSetsBonus ?? 10} points
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Final Set Tiebreak</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  +{tc?.tieBreakerBonus ?? 10} points
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Dominant Set (6-0 or 6-1)</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  +{tc?.dominantSetPoints ?? 10} points
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Upset Bonus (5+ seed diff)</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  +{tc?.upsetSmallBonus ?? 10} points
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Major Upset (15+ seed diff)</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  +{tc?.upsetLargeBonus ?? 16} points
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Correct Prediction</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  +{tc?.predictionBonus ?? 10} points
                </Typography>
              </Grid>
              <Grid size={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 1 }}>
                  Slot Multipliers
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.75} mb={0.5}>
                  {menMults.map((m, i) => (
                    <Chip
                      key={`m${i}`}
                      label={`M${i + 1}: ${m.toFixed(2)}x`}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' }, fontWeight: 'bold' }}
                    />
                  ))}
                </Box>
                <Box display="flex" flexWrap="wrap" gap={0.75}>
                  {womenMults.map((m, i) => (
                    <Chip
                      key={`w${i}`}
                      label={`W${i + 1}: ${m.toFixed(2)}x`}
                      color="secondary"
                      variant="outlined"
                      size="small"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' }, fontWeight: 'bold' }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Runs</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  1 run = 1 point
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Wickets</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  1 wicket = {league.format === 'Test' ? '20' : '25'} points
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Catches</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  1 catch = 5 points
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Correct Prediction</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {league.predictionWinningPoints ?? 80} points
                </Typography>
              </Grid>
              <Grid size={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5 }}>
                  Role Multipliers
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  <Chip label="Captain: 2x points" color="warning" size="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', px: 1.5, boxShadow: '0 0 8px rgba(255,193,7,0.35)' }} />
                  <Chip label="Vice-Captain: 1.5x points" color="info" size="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', px: 1.5, boxShadow: '0 0 8px rgba(2,136,209,0.35)' }} />
                  <Chip label="X-Factor: 1.25x points" color="secondary" size="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', px: 1.5, boxShadow: '0 0 8px rgba(156,39,176,0.35)' }} />
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Basic Info */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2, md: 2.5 } }}>
          <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }} mb={{ xs: 1.5, sm: 2 }}>
            {isTennis
              ? <SportsTennis color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
              : <SportsCricket color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />}
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              League Information
            </Typography>
          </Box>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>League Name</Typography>
              <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{league.name}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Tournament</Typography>
              <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{league.tournamentName}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Format</Typography>
              <Chip label={league.format} size="small" color="primary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' }, height: { xs: 20, sm: 24 } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</Typography>
              <Chip label={league.status.replace('_', ' ')} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' }, height: { xs: 20, sm: 24 } }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Important Dates */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2, md: 2.5 } }}>
          <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }} mb={{ xs: 1.5, sm: 2 }}>
            <CalendarMonth color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Important Dates
            </Typography>
          </Box>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>League Start Date</Typography>
              <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {new Date(league.startDate).toLocaleString()}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Squad Selection Deadline</Typography>
              <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {new Date(league.squadDeadline).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Squad Rules */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2, md: 2.5 } }}>
          <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }} mb={{ xs: 1.5, sm: 2 }}>
            <People color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Squad Rules
            </Typography>
          </Box>

          {isTennis ? (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Men's Slots</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {tr?.menSlots ?? 5} players (M1 to M{tr?.menSlots ?? 5})
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Women's Slots</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {tr?.womenSlots ?? 5} players (W1 to W{tr?.womenSlots ?? 5})
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Total Players</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {(tr?.menSlots ?? 5) + (tr?.womenSlots ?? 5)} players
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Max Participants</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {league.maxParticipants} teams
                </Typography>
              </Grid>
              {tr?.budget && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Buying Purse</Typography>
                  <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {tr.budget} credits per user
                  </Typography>
                </Grid>
              )}
              <Grid size={12}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontStyle: 'italic' }}>
                  Higher-ranked slots (M1, W1) earn larger point multipliers.{tr?.budget ? ' Spend your credits wisely — budget is shared across all 10 picks.' : ''}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Squad Size</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {league.squadSize} players
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Max Participants</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {league.maxParticipants} teams
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Min Batsmen</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {league.squadRules.minBatsmen}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Min Bowlers</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {league.squadRules.minBowlers}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Min All-rounders</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {league.squadRules.minAllrounders}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Min Wicketkeepers</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {league.squadRules.minWicketkeepers}
                </Typography>
              </Grid>
              {league.squadRules.hasBudget && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Budget</Typography>
                  <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {league.squadRules.totalBudget} credits
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Transfer Settings */}
      {league.transferTypes && (
        <Card sx={{ mb: { xs: 2, sm: 3 } }}>
          <CardContent sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2, md: 2.5 } }}>
            <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }} mb={{ xs: 1.5, sm: 2 }}>
              <SwapHoriz color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Transfer Settings
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" mb={{ xs: 1.5, sm: 2 }} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Total transfers allowed: {' '}
              {(league.transferTypes.benchTransfers.enabled ? league.transferTypes.benchTransfers.maxAllowed : 0) +
               (league.transferTypes.midSeasonTransfers.enabled ? league.transferTypes.midSeasonTransfers.maxAllowed : 0) +
               (league.transferTypes.flexibleTransfers.enabled ? league.transferTypes.flexibleTransfers.maxAllowed : 0) +
               (league.transferTypes.wildcardTransfers?.enabled ? league.transferTypes.wildcardTransfers.maxAllowed : 0)}
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={{ xs: 0.75, sm: 1 }} mb={{ xs: 1.5, sm: 2 }}>
              {league.transferTypes.benchTransfers.enabled && (
                <Chip
                  label={`Bench: ${league.transferTypes.benchTransfers.maxAllowed} allowed`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                />
              )}
              {league.transferTypes.midSeasonTransfers.enabled && (
                <Chip
                  label={`Mid-Season: ${league.transferTypes.midSeasonTransfers.maxAllowed} allowed`}
                  color="secondary"
                  variant="outlined"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                />
              )}
              {league.transferTypes.flexibleTransfers.enabled && (
                <Chip
                  label={`Flexible: ${league.transferTypes.flexibleTransfers.maxAllowed} allowed`}
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                />
              )}
              {league.transferTypes.wildcardTransfers?.enabled && (
                <Chip
                  label={`Wildcard: ${league.transferTypes.wildcardTransfers.maxAllowed} allowed`}
                  variant="outlined"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                    borderColor: '#F59E0B',
                    color: '#F59E0B',
                  }}
                />
              )}
            </Box>

            <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

            {/* Bench Transfers Details */}
            {league.transferTypes.benchTransfers.enabled && (
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 1 }}>
                  Bench Change
                </Typography>
                <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 1 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Bench Slots</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {league.transferTypes.benchTransfers.benchSlots}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Max Bench Transfers</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {league.transferTypes.benchTransfers.maxAllowed}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5 }}>
                    Player Substitution
                  </Typography>
                  <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                      Swap any main team player with one of your bench players
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                      <strong>Can replace ANY player including Captain, Vice-Captain, and X-Factor</strong>
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                      <strong>Role assignment:</strong> When swapping out C/VC/X, the incoming bench player automatically gets that role (roles may swap if incoming player already has a different role)
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                      Pure substitution only - no manual role changes allowed
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Flexible Transfers Details */}
            {league.transferTypes.flexibleTransfers.enabled && (
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 1 }}>
                  Flexible Change
                </Typography>
                <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 1 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Max Allowed</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {league.transferTypes.flexibleTransfers.maxAllowed}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 1.5, lineHeight: 1.6, color: 'white' }}>
                  Full flexibility except Captain. You can make <strong>ONE</strong> of the following two changes:
                </Typography>

                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5, color: 'white' }}>
                    A. Player Substitution
                  </Typography>
                  <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      Replace any player with a new player from the available pool
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      <strong>Can replace Vice-Captain and X-Factor, but Captain cannot be removed</strong>
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      All squad composition rules must still be met
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5, color: 'white' }}>
                    B. Role Assignment
                  </Typography>
                  <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      Keep all players the same
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      <strong>Reassign Vice-Captain or X-Factor to any other player</strong>
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      <strong>Captain cannot be changed</strong>
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      Points multipliers apply only to future points earned after reassignment
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Wildcard Transfers Details */}
            {league.transferTypes.wildcardTransfers?.enabled && (
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 1 }}>
                  Wildcard Change
                </Typography>
                <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 1 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Max Allowed</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {league.transferTypes.wildcardTransfers.maxAllowed}
                    </Typography>
                  </Grid>
                </Grid>
                <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: '#F59E0B' }}>
                  <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      Replace any player (including Captain) with any player from the full pool
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      <strong>Reassign any role</strong> (C / VC / X) to any player
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      Most powerful transfer — no restrictions on who can be changed
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Mid-Season Transfers Details */}
            {league.transferTypes.midSeasonTransfers.enabled && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 1 }}>
                  Mid-Season Change
                </Typography>
                <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 1 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Window Opens</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {new Date(league.transferTypes.midSeasonTransfers.windowStartDate).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Window Closes</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {new Date(league.transferTypes.midSeasonTransfers.windowEndDate).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Max Transfers</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {league.transferTypes.midSeasonTransfers.maxAllowed}
                    </Typography>
                  </Grid>
                </Grid>
                <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 1.5, lineHeight: 1.6, color: 'white' }}>
                    A Mid-Season Change works exactly like a Flexible Change, but with a timing restriction.
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      Follows all the same rules as a Flexible Change (see above)
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      <strong>Timing restriction:</strong> Only allowed during the specific mid-season window announced by the league
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6, color: 'white' }}>
                      Helps teams rebalance mid-tournament based on player form and injuries
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Special Features */}
      {(league.powerplayEnabled || league.hiddenPlayerEnabled) && (
        <Card sx={{ mb: { xs: 2, sm: 3 } }}>
          <CardContent sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2, md: 2.5 } }}>
            <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }} mb={{ xs: 1.5, sm: 2 }}>
              <EmojiEvents color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Special Features
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {league.powerplayEnabled && (
                <Grid size={12}>
                  <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Bolt fontSize="small" />
                      {league.ppMatchMode === 'activation' ? 'On-Demand Powerplay' : 'Powerplay'}
                    </Typography>
                    {league.ppMatchMode === 'activation' ? (
                      <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                        <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                          You can activate Powerplay anytime during the league, just like transfers.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                          Once activated, pick one upcoming match from fixtures scheduled after your activation time.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                          This is a one-time action and <strong>cannot be undone once activated</strong>.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, lineHeight: 1.6 }}>
                          After selection, your Powerplay match stays locked for the rest of the league.
                        </Typography>
                      </Box>
                    ) : (
                      <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                        <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                          Pick one Powerplay match from the allowed fixtures shown for this league.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, lineHeight: 1.6 }}>
                          Powerplay bonus points are applied to that selected match as per league scoring rules.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {league.hiddenPlayerEnabled && (
                <Grid size={12}>
                  <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Lock fontSize="small" />
                      12th Hidden Player
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                      <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                        Each participant picks one extra player as their 12th Hidden Player.
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                        This player is outside the visible squad and remains hidden from other participants.
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5, lineHeight: 1.6 }}>
                        You can track your own hidden player, but others cannot view your selection.
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, lineHeight: 1.6 }}>
                        At season end, the admin applies hidden player points to the final leaderboard.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default LeagueRulesDisplay;
